import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, DollarSign, Mail, UserPlus, Send, FileText, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { money, moneyK, fecha, hoy, Badge, Empty, ESTADO_COBRO, ESTADO_RENOVACION } from './ui';

const TIPO_CONTRATO = { abono: 'Abono mensual', anual: 'Renovación anual', proyecto: 'Proyecto', unico: 'Cobro único' };

export default function AgenciaClienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [listas, setListas] = useState({ servicios: [], medios: [], agencias: [] });
  const [saving, setSaving] = useState(false);
  const [cobroNuevo, setCobroNuevo] = useState(null);
  const [contratoNuevo, setContratoNuevo] = useState(null);
  const [pago, setPago] = useState(null);
  const [contactoNuevo, setContactoNuevo] = useState(null);
  const [factura, setFactura] = useState(null);

  const load = useCallback(() => {
    api.get('/agency/clientes/' + id).then(r => { setD(r.data); setForm(r.data.cliente); }).catch(() => toast.error('No se pudo cargar'));
  }, [id]);
  useEffect(() => { load(); api.get('/agency/listas').then(r => setListas(r.data)).catch(() => {}); }, [load]);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const guardar = async () => {
    setSaving(true);
    try { await api.put('/agency/clientes/' + id, form); toast.success('Guardado'); load(); }
    catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };
  const crearCobro = async (e) => {
    e.preventDefault();
    try { await api.post('/agency/cobros', { ...cobroNuevo, cliente_id: id }); toast.success('Cobro creado'); setCobroNuevo(null); load(); }
    catch { toast.error('Error'); }
  };
  const crearContrato = async (e) => {
    e.preventDefault();
    try {
      if (contratoNuevo.id) await api.put('/agency/contratos/' + contratoNuevo.id, contratoNuevo);
      else await api.post('/agency/contratos', { ...contratoNuevo, cliente_id: id });
      toast.success(contratoNuevo.id ? 'Contrato actualizado' : 'Contrato creado'); setContratoNuevo(null); load();
    }
    catch { toast.error('Error'); }
  };
  const crearContacto = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/agency/clientes/${id}/contactos`, contactoNuevo);
      toast.success(contactoNuevo.invitar ? 'Usuario creado y acceso enviado por email' : 'Usuario creado');
      setContactoNuevo(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  const invitar = async (c) => {
    try { await api.post(`/agency/contactos/${c.id}/invitar`); toast.success('Acceso enviado a ' + c.email); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'No se pudo enviar', { duration: 7000 }); }
  };
  const cambiarContacto = async (c, cambios) => {
    try { await api.put('/agency/contactos/' + c.id, cambios); load(); } catch { toast.error('Error'); }
  };
  const guardarFactura = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      ['numero', 'tipo', 'fecha', 'monto'].forEach(k => factura[k] && fd.append(k, factura[k]));
      fd.append('enviar', factura.enviar ? 'true' : 'false');
      if (factura.archivo) fd.append('archivo', factura.archivo);
      await api.post(`/agency/cobros/${factura.cobro.id}/factura`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(factura.enviar ? 'Factura guardada y enviada' : 'Factura guardada');
      setFactura(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  const registrarPago = async (e) => {
    e.preventDefault();
    try { await api.post(`/agency/cobros/${pago.id}/pagos`, pago); toast.success('Pago registrado'); setPago(null); load(); }
    catch { toast.error('Error'); }
  };

  const totalFacturado = d.por_anio.reduce((a, x) => a + Number(x.total), 0);
  const pendiente = d.cobros.filter(c => c.estado !== 'pagado' && c.estado !== 'anulado').reduce((a, c) => a + Number(c.monto), 0);
  const label = { fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <button className="btn btn-ghost" onClick={() => navigate('/agencia/clientes')} style={{ marginBottom: 14 }}><ArrowLeft size={16} /> Volver</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>{d.cliente.nombre}</h1>
        <Badge label={d.cliente.tipo === 'agencia' ? 'Agencia' : d.cliente.estado} color={d.cliente.estado === 'activo' ? '#22c55e' : 'var(--text-muted)'} />
        {d.cliente.agencia_nombre && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>traído por {d.cliente.agencia_nombre}</span>}
      </div>
      {d.cliente.nombres_anteriores && d.cliente.nombres_anteriores !== d.cliente.nombre && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>Antes figuraba como: {d.cliente.nombres_anteriores}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ padding: '12px 14px' }}><div style={label}>Facturado histórico</div><div style={{ fontSize: 22, fontWeight: 800 }}>{moneyK(totalFacturado)}</div></div>
        <div className="card" style={{ padding: '12px 14px' }}><div style={label}>Por cobrar</div><div style={{ fontSize: 22, fontWeight: 800, color: pendiente ? '#f59e0b' : 'var(--text-primary)' }}>{moneyK(pendiente)}</div></div>
        <div className="card" style={{ padding: '12px 14px' }}><div style={label}>Contratos activos</div><div style={{ fontSize: 22, fontWeight: 800 }}>{d.contratos.filter(c => c.estado === 'activo').length}</div></div>
        <div className="card" style={{ padding: '12px 14px' }}><div style={label}>Cobros</div><div style={{ fontSize: 22, fontWeight: 800 }}>{d.cobros.length}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20, alignItems: 'start' }}>
        {/* Datos */}
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Datos del cliente</h3>
          <div className="form-group"><label>Nombre</label><input value={form.nombre || ''} onChange={set('nombre')} /></div>
          <div className="form-group"><label>Razón social</label><input value={form.razon_social || ''} onChange={set('razon_social')} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>CUIT</label><input value={form.cuit || ''} onChange={set('cuit')} /></div>
            <div className="form-group"><label>Condición IVA</label>
              <select value={form.condicion_iva || ''} onChange={set('condicion_iva')}>
                <option value="">—</option><option>Responsable Inscripto</option><option>Monotributo</option><option>Exento</option><option>Consumidor Final</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label>Email de facturación</label><input type="email" value={form.email_facturacion || ''} onChange={set('email_facturacion')} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Teléfono</label><input value={form.telefono || ''} onChange={set('telefono')} /></div>
            <div className="form-group"><label>Web</label><input value={form.web || ''} onChange={set('web')} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Estado</label>
              <select value={form.estado || 'activo'} onChange={set('estado')}><option value="activo">Activo</option><option value="inactivo">Inactivo</option><option value="prospecto">Prospecto</option></select>
            </div>
            <div className="form-group"><label>Traído por</label>
              <select value={form.agencia_id || ''} onChange={set('agencia_id')}>
                <option value="">Directo</option>
                {listas.agencias.filter(a => String(a.id) !== String(id)).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label>Facturar a</label>
            <select value={form.facturar_a || 'cliente'} onChange={set('facturar_a')}><option value="cliente">Al cliente</option><option value="agencia">A la agencia</option></select>
          </div>
          <div className="form-group"><label>Notas internas</label><textarea rows={3} value={form.notas || ''} onChange={set('notas')} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
          <button className="btn btn-primary" onClick={guardar} disabled={saving}><Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}</button>
        </div>

        {/* Contratos */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Contratos y abonos</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setContratoNuevo({ tipo: 'abono', monto: '', dia_cobro: '' })}><Plus size={14} /> Agregar</button>
          </div>
          {d.contratos.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Concepto</th><th style={th}>Tipo</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Próximo</th></tr></thead>
              <tbody>{d.contratos.map(c => (
                <tr key={c.id} onClick={() => setContratoNuevo({ ...c, monto: String(Math.round(Number(c.monto))), proximo_vencimiento: c.proximo_vencimiento ? String(c.proximo_vencimiento).slice(0, 10) : '', ultima_actualizacion_precio: undefined })} style={{ cursor: 'pointer' }} title="Editar contrato">
                  <td style={td}><div style={{ fontWeight: 600 }}>{c.nombre || c.servicio || '—'}</div>
                    {c.ultima_actualizacion_precio && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>último ajuste {fecha(c.ultima_actualizacion_precio)}</div>}</td>
                  <td style={{ ...td, fontSize: 12 }}>{TIPO_CONTRATO[c.tipo]}{c.dia_cobro ? ` · día ${c.dia_cobro}` : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {c.tipo === 'anual' ? <>{fecha(c.proximo_vencimiento)}<br /><Badge label={(ESTADO_RENOVACION[c.estado_renovacion] || {}).label} color={(ESTADO_RENOVACION[c.estado_renovacion] || {}).color} /></> : (c.estado === 'activo' ? 'Activo' : c.estado)}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          ) : <Empty>Sin contratos cargados</Empty>}

          {d.subclientes.length > 0 && (<>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '20px 0 8px' }}>Clientes de esta agencia</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {d.subclientes.map(s => (
                <button key={s.id} className="btn btn-ghost btn-sm" onClick={() => navigate('/agencia/clientes/' + s.id)}>{s.nombre}</button>
              ))}
            </div>
          </>)}
        </div>
      </div>

      {/* Usuarios del portal */}
      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>Usuarios del portal</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setContactoNuevo({ email: '', nombre: '', ve_importes: d.cliente.facturar_a !== 'agencia', invitar: true })}><UserPlus size={14} /> Agregar usuario</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Entran en clientes.udream.app. {d.cliente.facturar_a === 'agencia' ? 'Como se factura a la agencia, por defecto no ven importes ni facturas.' : 'Ven sus facturas y pueden avisar los pagos.'}
        </div>
        {d.contactos.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Usuario</th><th style={th}>Estado</th><th style={th}>Ve importes</th><th style={th}>Último acceso</th><th style={th}></th></tr></thead>
            <tbody>{d.contactos.map(c => (
              <tr key={c.id}>
                <td style={td}><div style={{ fontWeight: 600 }}>{c.nombre || c.email}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div></td>
                <td style={td}><Badge label={{ sin_invitar: 'Sin invitar', invitado: 'Invitado', activo: 'Activo', desactivado: 'Desactivado' }[c.acceso]} color={c.acceso === 'activo' ? '#22c55e' : c.acceso === 'invitado' ? '#f59e0b' : 'var(--text-muted)'} /></td>
                <td style={td}><input type="checkbox" checked={!!c.ve_importes} onChange={() => cambiarContacto(c, { ve_importes: c.ve_importes ? 0 : 1 })} style={{ width: 16, height: 16, cursor: 'pointer' }} /></td>
                <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>{c.ultimo_acceso ? fecha(c.ultimo_acceso) : '—'}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => invitar(c)} title="Enviar acceso por email"><Send size={13} /> {c.acceso === 'sin_invitar' ? 'Enviar acceso' : 'Reenviar'}</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => cambiarContacto(c, { acceso: c.acceso === 'desactivado' ? 'invitado' : 'desactivado' })} style={{ color: 'var(--text-muted)' }}>{c.acceso === 'desactivado' ? 'Reactivar' : 'Desactivar'}</button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        ) : <Empty>Todavía no tiene acceso al portal</Empty>}
      </div>

      {/* Cobros */}
      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>Cobros y pagos</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setCobroNuevo({ monto: '', vencimiento: hoy(), estado: 'pendiente', concepto: '' })}><Plus size={14} /> Nuevo cobro</button>
        </div>
        {d.cobros.length ? (
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Concepto</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}>Factura</th><th style={th}></th></tr></thead>
              <tbody>{d.cobros.map(c => {
                const e = ESTADO_COBRO[c.estado] || {};
                return (
                  <tr key={c.id}>
                    <td style={td}><div style={{ fontWeight: 600 }}>{c.concepto || '—'}</div>{c.notas && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.notas}</div>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(c.vencimiento)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                    <td style={td}><Badge label={e.label} color={e.color} bg={e.bg} /></td>
                    <td style={td}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setFactura({ cobro: c, monto: c.monto, fecha: (c.vencimiento || '').slice(0, 10), enviar: true })} style={{ fontSize: 12, color: c.sin_factura ? 'var(--text-muted)' : 'var(--accent)' }}>
                        <FileText size={13} /> {c.sin_factura ? 'Cargar' : 'Factura'}
                      </button>
                    </td>
                    <td style={td}>{c.estado !== 'pagado' && <button className="btn btn-ghost btn-sm" onClick={() => setPago({ id: c.id, fecha: hoy(), monto: c.monto })}><DollarSign size={14} /> Pagó</button>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        ) : <Empty>Sin cobros registrados</Empty>}

        {d.por_anio.length > 0 && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, fontSize: 12, color: 'var(--text-muted)' }}>
            {d.por_anio.map(a => <span key={a.anio}><b style={{ color: 'var(--text-primary)' }}>{a.anio}:</b> {money(a.total)}</span>)}
          </div>
        )}
      </div>

      {(d.proyectos.length > 0 || d.propuestas.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20, marginTop: 20 }}>
          {d.proyectos.length > 0 && (
            <div className="card"><h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Proyectos</h3>
              {d.proyectos.map(p => <div key={p.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                <b>{p.nombre}</b> <span style={{ color: 'var(--text-muted)' }}>· {p.estado.replace('_', ' ')}</span>
              </div>)}
            </div>
          )}
          {d.propuestas.length > 0 && (
            <div className="card"><h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Propuestas</h3>
              {d.propuestas.map(p => <div key={p.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                <b>{p.titulo}</b> <span style={{ color: 'var(--text-muted)' }}>· {p.estado.replace('_', ' ')}</span>
              </div>)}
            </div>
          )}
        </div>
      )}

      {cobroNuevo && (
        <div className="modal-overlay" onClick={() => setCobroNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Nuevo cobro</h2>
            <form onSubmit={crearCobro}>
              <div className="form-group"><label>Concepto</label><input value={cobroNuevo.concepto} onChange={e => setCobroNuevo({ ...cobroNuevo, concepto: e.target.value })} placeholder="Ej: Abono septiembre" required /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Monto</label><input type="number" step="0.01" value={cobroNuevo.monto} onChange={e => setCobroNuevo({ ...cobroNuevo, monto: e.target.value })} required /></div>
                <div className="form-group"><label>Vence</label><input type="date" value={cobroNuevo.vencimiento} onChange={e => setCobroNuevo({ ...cobroNuevo, vencimiento: e.target.value })} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Servicio</label>
                  <select value={cobroNuevo.servicio_id || ''} onChange={e => setCobroNuevo({ ...cobroNuevo, servicio_id: e.target.value })}>
                    <option value="">—</option>{listas.servicios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Estado</label>
                  <select value={cobroNuevo.estado} onChange={e => setCobroNuevo({ ...cobroNuevo, estado: e.target.value })}>
                    <option value="pendiente">Pendiente</option><option value="pagado">Ya pagado</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setCobroNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {contratoNuevo && (
        <div className="modal-overlay" onClick={() => setContratoNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>{contratoNuevo.id ? 'Editar contrato' : 'Nuevo contrato'}</h2>
            <form onSubmit={crearContrato}>
              <div className="form-group"><label>Concepto</label><input value={contratoNuevo.nombre || ''} onChange={e => setContratoNuevo({ ...contratoNuevo, nombre: e.target.value })} placeholder="Ej: Google Ads" required /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Tipo</label>
                  <select value={contratoNuevo.tipo} onChange={e => setContratoNuevo({ ...contratoNuevo, tipo: e.target.value })}>
                    <option value="abono">Abono mensual</option><option value="anual">Renovación anual</option><option value="proyecto">Proyecto</option><option value="unico">Cobro único</option>
                  </select>
                </div>
                <div className="form-group"><label>Monto</label><input type="number" step="0.01" value={contratoNuevo.monto} onChange={e => setContratoNuevo({ ...contratoNuevo, monto: e.target.value })} required /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>{contratoNuevo.tipo === 'anual' ? 'Mes de renovación' : 'Día de cobro'}</label>
                  <input type="number" min="1" max={contratoNuevo.tipo === 'anual' ? 12 : 31}
                    value={contratoNuevo.tipo === 'anual' ? (contratoNuevo.mes_renovacion || '') : (contratoNuevo.dia_cobro || '')}
                    onChange={e => setContratoNuevo({ ...contratoNuevo, [contratoNuevo.tipo === 'anual' ? 'mes_renovacion' : 'dia_cobro']: e.target.value })} />
                </div>
                <div className="form-group"><label>Servicio</label>
                  <select value={contratoNuevo.servicio_id || ''} onChange={e => setContratoNuevo({ ...contratoNuevo, servicio_id: e.target.value })}>
                    <option value="">—</option>{listas.servicios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
              </div>
              {contratoNuevo.id && (
                <div className="form-group"><label>Estado</label>
                  <select value={contratoNuevo.estado || 'activo'} onChange={e => setContratoNuevo({ ...contratoNuevo, estado: e.target.value })}>
                    <option value="activo">Activo</option><option value="pausado">Pausado</option><option value="baja">Dado de baja</option>
                  </select>
                </div>
              )}
              {contratoNuevo.id && Number(contratoNuevo.monto) !== Number(d.contratos.find(c => c.id === contratoNuevo.id)?.monto || 0) && (
                <div style={{ fontSize: 12, color: '#f59e0b', marginTop: -6, marginBottom: 12 }}>
                  Cambiaste el monto: se va a registrar como ajuste de precio con fecha de hoy.
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setContratoNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{contratoNuevo.id ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {contactoNuevo && (
        <div className="modal-overlay" onClick={() => setContactoNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Nuevo usuario del portal</h2>
            <form onSubmit={crearContacto}>
              <div className="form-group"><label>Email *</label><input type="email" value={contactoNuevo.email} onChange={e => setContactoNuevo({ ...contactoNuevo, email: e.target.value })} required autoFocus /></div>
              <div className="form-group"><label>Nombre</label><input value={contactoNuevo.nombre} onChange={e => setContactoNuevo({ ...contactoNuevo, nombre: e.target.value })} /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!contactoNuevo.ve_importes} onChange={e => setContactoNuevo({ ...contactoNuevo, ve_importes: e.target.checked })} style={{ width: 16, height: 16 }} />
                Puede ver importes y facturas
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!contactoNuevo.invitar} onChange={e => setContactoNuevo({ ...contactoNuevo, invitar: e.target.checked })} style={{ width: 16, height: 16 }} />
                <Mail size={14} /> Enviarle el acceso por email ahora
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setContactoNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {factura && (
        <div className="modal-overlay" onClick={() => setFactura(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Factura del cobro</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 14px' }}>{factura.cobro.concepto} · {money(factura.cobro.monto)}</p>
            <form onSubmit={guardarFactura}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr', gap: 10 }}>
                <div className="form-group"><label>Número</label><input value={factura.numero || ''} onChange={e => setFactura({ ...factura, numero: e.target.value })} placeholder="0001-00001234" /></div>
                <div className="form-group"><label>Tipo</label>
                  <select value={factura.tipo || ''} onChange={e => setFactura({ ...factura, tipo: e.target.value })}><option value="">—</option><option>A</option><option>B</option><option>C</option></select>
                </div>
                <div className="form-group"><label>Fecha</label><input type="date" value={factura.fecha || ''} onChange={e => setFactura({ ...factura, fecha: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Archivo PDF</label>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}><Upload size={14} /> {factura.archivo ? factura.archivo.name.slice(0, 28) : 'Elegir PDF'}
                  <input type="file" accept="application/pdf,image/*" onChange={e => setFactura({ ...factura, archivo: e.target.files[0] })} style={{ display: 'none' }} /></label>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!factura.enviar} onChange={e => setFactura({ ...factura, enviar: e.target.checked })} style={{ width: 16, height: 16 }} />
                <Mail size={14} /> Enviarla por email al cliente
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setFactura(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pago && (
        <div className="modal-overlay" onClick={() => setPago(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>Registrar pago</h2>
            <form onSubmit={registrarPago}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Fecha</label><input type="date" value={pago.fecha} onChange={e => setPago({ ...pago, fecha: e.target.value })} /></div>
                <div className="form-group"><label>Monto</label><input type="number" step="0.01" value={pago.monto} onChange={e => setPago({ ...pago, monto: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Medio de pago</label>
                <select value={pago.medio_id || ''} onChange={e => setPago({ ...pago, medio_id: e.target.value })}>
                  <option value="">—</option>{listas.medios.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Registrar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
